import React from 'react';
import { useTranslation } from 'react-i18next';
import CgPopover from './CgPopover';
import { TableCountry } from './Country';

/**
 * A generalized notification bubble component that can display various types of data
 * in a popover when hovered. Perfect for navbar notifications.
 *
 * @param {number} count - Total count to display in the bubble
 * @param {string} title - Main title for the popover
 * @param {string} subtitle - Optional subtitle text
 * @param {Array} sections - Array of data sections to display
 * @param {string} className - Additional CSS classes
 * @param {number} maxItemsPerSection - Maximum items to show per section (default: 3)
 * @param {string} maxHeight - CSS class for maximum height (default: "max-h-64")
 *
 * Section structure:
 * {
 *   title: string,           // Section title (e.g., "Declarant Requests")
 *   count: number,           // Count for this section
 *   color: string,           // Tailwind color class for the indicator dot
 *   bgColor: string,         // Background color class for items
 *   borderColor: string,     // Border color class for items
 *   items: Array<{
 *     name: string,          // Primary display name
 *     subtitle?: string,     // Optional subtitle
 *     country?: {            // Optional country info for TableCountry component
 *       name: string,
 *       iso2: string,
 *       iso3: string
 *     },
 *     customContent?: React.ReactNode  // Optional custom content
 *   }>
 * }
 */
const NotificationBubble = ({
  count,
  title,
  subtitle,
  sections = [],
  className = '',
  maxItemsPerSection = 3,
  maxHeight = 'max-h-64',
  position = 'right',
}) => {
  const { t } = useTranslation('common');
  if (count === 0) return null;

  const renderPopoverContent = () => {
    if (!sections || sections.length === 0) {
      return (
        <p className="text-sm text-gray-500">
          {t('common:notificationBubble.noItemsFound', 'No items found')}
        </p>
      );
    }

    return (
      <div className="max-w-lg">
        <div className="mb-3">
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>

        <div className={`space-y-3 ${maxHeight} overflow-y-auto`}>
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${section.color || 'bg-gray-500'}`}
                ></span>
                {section.title} ({section.count})
              </h5>
              <div className="space-y-2">
                {section.items.slice(0, maxItemsPerSection).map((item, itemIndex) => (
                  <div
                    key={`${sectionIndex}-${itemIndex}`}
                    className={`p-2 border rounded-md ${section.bgColor || 'bg-gray-50'} ${section.borderColor || 'border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.name || t('common:notificationBubble.unknown', 'Unknown')}
                        </p>
                        {item.subtitle && (
                          <p className="text-xs text-gray-500 mt-1">{item.subtitle}</p>
                        )}
                        {item.country && (
                          <div className="flex items-center mt-1">
                            <TableCountry
                              nameOut
                              name={item.country.name}
                              iso2={item.country.iso2}
                              iso3={item.country.iso3}
                            />
                          </div>
                        )}
                        {item.customContent && <div className="mt-1">{item.customContent}</div>}
                      </div>
                    </div>
                  </div>
                ))}
                {section.items.length > maxItemsPerSection && (
                  <div className="text-xs text-gray-500 text-center py-1">
                    {t('common:notificationBubble.moreItemsInSection', {
                      count: section.items.length - maxItemsPerSection,
                      section: section.title.toLowerCase(),
                      defaultValue: '+{{count}} more {{section}}',
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <CgPopover
      triggerElement={
        <div className={`relative inline-block ${className}`}>
          <div
            className="bg-red-600 text-white rounded-full min-w-[16px] h-[16px] flex items-center justify-center text-xs font-bold animate-pulse cursor-pointer hover:bg-red-700 transition-all duration-200 shadow-sm transform hover:scale-110"
            style={{
              fontSize: '9px',
              lineHeight: '1',
              padding: count > 99 ? '1px 3px' : '1px',
            }}
          >
            {count > 99 ? '99+' : count}
          </div>
        </div>
      }
      popoverContent={renderPopoverContent()}
      trigger="hover"
      position={position}
      openDelay={200}
      closeDelay={300}
      popoverContentClassName="border-gray-200 shadow-lg bg-white"
    />
  );
};

export default NotificationBubble;
